import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", price_inr: 0, billing: "monthly", trial_days: 0, active: true, description: "", features: {}, limits: { invoices: -1, customers: -1, items: -1, users: 1 } });

  const load = async () => setPlans((await api.get("/admin/plans")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: "", name: "", price_inr: 0, billing: "monthly", trial_days: 0, active: true, description: "", features: {}, limits: { invoices: -1, customers: -1, items: -1, users: 1 } });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/plans/${editing.id}`, form);
      } else {
        await api.post("/admin/plans", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate plan?")) return;
    await api.delete(`/admin/plans/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Plans</h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage subscription plans</p>
        </div>
        <Button onClick={openCreate} data-testid="admin-add-plan"><Plus size={14} className="mr-1" /> Create Plan</Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Billing</TableHead>
              <TableHead>Trial Days</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.code}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.price_inr === 0 ? "Free" : inr(p.price_inr)}</TableCell>
                <TableCell>{p.billing}</TableCell>
                <TableCell>{p.trial_days || 0}</TableCell>
                <TableCell>
                  <Badge variant={p.active ? "default" : "outline"}>{p.active ? "Active" : "Inactive"}</Badge>
                  {p.featured && <Badge className="ml-1" variant="secondary">Featured</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.code}`}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-destructive">Deactivate</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Create"} Plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="plan-code" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" /></div>
            <div><Label>Price (₹)</Label><Input type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: parseInt(e.target.value) || 0 })} data-testid="plan-price" /></div>
            <div>
              <Label>Billing</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })}>
                {["monthly", "yearly", "trial", "one-time"].map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div><Label>Trial Days</Label><Input type="number" value={form.trial_days || 0} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value) || 0 })} /></div>
            <div>
              <Label>Featured</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.featured ? "yes" : "no"} onChange={(e) => setForm({ ...form, featured: e.target.value === "yes" })}>
                <option value="no">No</option><option value="yes">Yes</option>
              </select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="plan-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
